import { useState } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import {
  ArrowRight,
  Bell,
  BookOpen,
  Bot,
  CalendarCheck,
  CheckCircle2,
  ChevronDown,
  CircleDashed,
  EyeOff,
  GitPullRequest,
  Hammer,
  History,
  Inbox,
  ListPlus,
  MessageCircleQuestion,
  MessageSquare,
  MessageSquareText,
  MousePointerClick,
  Reply,
  RotateCcw,
  Send,
  ThumbsDown,
  ThumbsUp,
  Undo2,
  XCircle,
} from 'lucide-react';
import { Skeleton } from '@/components/ui/Skeleton';
import { Tooltip } from '@/components/ui/Tooltip';
import { cn } from '@/components/ui/cn';
import type { KlikContext } from '@/lib/gebruiksEvents';
import {
  useBouwpuntGebeurtenissen,
  useMeldingGebeurtenissen,
} from '../hooks/useMeldingGebeurtenissen';
import {
  absoluteTijd,
  filterVoorRol,
  gebeurtenisKop,
  leesDetails,
  relatieveTijd,
  soortDefinitie,
  sorteerGebeurtenissen,
  type Gebeurtenis,
  type GebeurtenisSoort,
  type GebeurtenisToon,
  type TijdlijnRol,
} from '../lib/gebeurtenissen';

type Icoon = typeof Send;

const ICOON: Record<GebeurtenisSoort, Icoon> = {
  ingestuurd: Send,
  doorvraag_antwoord: MessageSquareText,
  vraag_ai: Bot,
  vraag_terug: MessageCircleQuestion,
  antwoord: Reply,
  herinnering: Bell,
  onvolledig: CircleDashed,
  aangevuld: Inbox,
  gepland: CalendarCheck,
  in_bouw: Hammer,
  uit_bouw: Undo2,
  pr_gekoppeld: GitPullRequest,
  opgelost: CheckCircle2,
  afgewezen: XCircle,
  heropend: RotateCcw,
  teruggezet: Undo2,
  feedback: MessageSquare,
  uitleg: BookOpen,
  beoordeling: MessageSquareText,
  aangemaakt: ListPlus,
  status: ArrowRight,
  commentaar: EyeOff,
};

/** Stip- en icoonkleur per toon; alleen status- en merktokens. */
const TOON: Record<GebeurtenisToon, { dot: string; icoon: string }> = {
  neutraal: { dot: 'bg-surface-tertiary ring-theme-border', icoon: 'text-foreground-secondary' },
  info: { dot: 'bg-info/15 ring-info/30', icoon: 'text-info' },
  warning: { dot: 'bg-warning/15 ring-warning/30', icoon: 'text-warning' },
  brand: { dot: 'bg-brand/15 ring-brand/30', icoon: 'text-brand' },
  success: { dot: 'bg-success/15 ring-success/30', icoon: 'text-success' },
  error: { dot: 'bg-error/15 ring-error/30', icoon: 'text-error' },
};

function icoonVoor(g: Gebeurtenis): Icoon {
  if (g.soort === 'feedback') {
    const w = leesDetails(g.details).feedback;
    if (w === 'werkt' || w === 'begrepen') return ThumbsUp;
    if (w === 'werkt_niet' || w === 'niet_begrepen') return ThumbsDown;
  }
  return ICOON[g.soort as GebeurtenisSoort] ?? MessageSquare;
}

interface TijdlijnProps {
  items: readonly Gebeurtenis[] | undefined;
  laden: boolean;
  rol: TijdlijnRol;
  compact?: boolean;
  leegTekst?: string;
}

/**
 * De tijdlijn zelf: verticale lijn met een stip + icoon per gebeurtenis, de
 * kop, wie en wanneer (relatief, absoluut in de tooltip), de tekst en een
 * PR-chip. Interne regels dragen een slotje en komen alleen bij
 * beheerder/meekijker binnen (de RLS filtert al; dit is de tweede poort).
 */
export function Tijdlijn({ items, laden, rol, compact = false, leegTekst }: TijdlijnProps) {
  const reduceer = useReducedMotion();
  if (laden && !items) {
    return (
      <div className="space-y-2" aria-busy="true">
        <Skeleton className="h-4 w-3/4" />
        <Skeleton className="h-4 w-1/2" />
        <Skeleton className="h-4 w-2/3" />
      </div>
    );
  }
  const zichtbaar = sorteerGebeurtenissen(filterVoorRol(items ?? [], rol));
  if (zichtbaar.length === 0) {
    return (
      <p className={cn('italic text-foreground-tertiary', compact ? 'text-[11px]' : 'text-sm')}>
        {leegTekst ?? 'Nog geen geschiedenis.'}
      </p>
    );
  }

  return (
    <ol className={cn('relative', compact ? 'space-y-1.5 pl-5' : 'space-y-3 pl-7')}>
      <span
        aria-hidden
        className={cn(
          'absolute top-1 bottom-1 w-px bg-theme-border',
          compact ? 'left-[7px]' : 'left-[11px]',
        )}
      />
      {zichtbaar.map((g, i) => {
        const def = soortDefinitie(g.soort);
        const toon = TOON[def.toon];
        const Icoon = icoonVoor(g);
        const details = leesDetails(g.details);
        const pr = details.pr_url ?? null;
        const tekst = g.tekst?.trim() ?? '';
        return (
          <motion.li
            key={g.id}
            initial={reduceer ? false : { opacity: 0, y: 4 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.18, delay: Math.min(i, 12) * 0.03 }}
            className="relative"
          >
            <span
              aria-hidden
              className={cn(
                'absolute flex items-center justify-center rounded-full ring-1',
                toon.dot,
                compact ? '-left-5 top-0 h-4 w-4' : '-left-7 top-0 h-6 w-6',
              )}
            >
              <Icoon className={cn(toon.icoon, compact ? 'h-2.5 w-2.5' : 'h-3.5 w-3.5')} />
            </span>
            <div
              className={cn(
                'flex flex-wrap items-baseline gap-x-1.5',
                compact ? 'text-[11px]' : 'text-sm',
              )}
            >
              <span className="font-semibold text-foreground">{gebeurtenisKop(g)}</span>
              {g.intern && (
                <Tooltip content="Intern — de melder ziet deze regel niet">
                  <span className="inline-flex items-center gap-0.5 rounded-md bg-warning/10 px-1 text-[10px] font-medium text-warning">
                    <EyeOff className="h-3 w-3" /> intern
                  </span>
                </Tooltip>
              )}
              <span className={cn('text-foreground-tertiary', compact ? 'text-[10px]' : 'text-xs')}>
                {g.door_naam ?? 'onbekend'} ·{' '}
                <Tooltip content={absoluteTijd(g.created_at)}>
                  <time
                    dateTime={g.created_at}
                    className="cursor-default underline decoration-dotted underline-offset-2"
                  >
                    {relatieveTijd(g.created_at)}
                  </time>
                </Tooltip>
              </span>
            </div>
            {tekst && (
              <p
                className={cn(
                  'mt-0.5 whitespace-pre-line text-foreground-secondary',
                  compact ? 'line-clamp-3 text-[11px]' : 'text-sm',
                )}
              >
                {tekst}
              </p>
            )}
            {pr && (
              <a
                href={pr}
                target="_blank"
                rel="noreferrer"
                className="mt-1 inline-flex max-w-full items-center gap-1 rounded-md bg-brand/10 px-1.5 py-0.5 text-[11px] font-medium text-brand hover:bg-brand/20"
              >
                <GitPullRequest className="h-3 w-3 shrink-0" />
                <span className="truncate">
                  {pr.replace(/^https?:\/\/(www\.)?github\.com\//, '')}
                </span>
              </a>
            )}
            {details.pr_nummers && g.soort === 'pr_gekoppeld' && (
              <span className="mt-1 inline-flex items-center gap-1 rounded-md bg-brand/10 px-1.5 py-0.5 text-[11px] font-medium text-brand">
                <GitPullRequest className="h-3 w-3" /> PR {details.pr_nummers}
              </span>
            )}
          </motion.li>
        );
      })}
    </ol>
  );
}

/** Tijdlijn van een melding (Mijn acties, bouwlijst-paneel, Mijn Inbox). */
export default function MeldingTijdlijn({
  meldingId,
  rol,
  compact,
}: {
  meldingId: string;
  rol: TijdlijnRol;
  compact?: boolean;
}) {
  const { data, isLoading } = useMeldingGebeurtenissen(meldingId);
  return <Tijdlijn items={data} laden={isLoading} rol={rol} compact={compact} />;
}

/** Tijdlijn van een bouwpunt (tom_build_reviews). */
export function BouwpuntTijdlijn({ bouwpuntId, rol }: { bouwpuntId: string; rol: TijdlijnRol }) {
  const { data, isLoading } = useBouwpuntGebeurtenissen(bouwpuntId);
  return <Tijdlijn items={data} laden={isLoading} rol={rol} />;
}

/**
 * Ingeklapte variant voor "Mijn meldingen" in de meldknop: één regel
 * "Geschiedenis", opent op klik en laadt pas dán (twintig meldingen in de
 * lijst = anders twintig queries bij het openen van het venster).
 */
export function MeldingTijdlijnCompact({ meldingId }: { meldingId: string }) {
  const [open, setOpen] = useState(false);
  const { data, isLoading } = useMeldingGebeurtenissen(meldingId, open);
  return (
    <div className="mt-1.5">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        className="inline-flex items-center gap-1 text-[11px] font-medium text-foreground-tertiary hover:text-foreground"
      >
        <History className="h-3 w-3" />
        Geschiedenis
        <ChevronDown className={cn('h-3 w-3 transition-transform', open && 'rotate-180')} />
      </button>
      {open && (
        <div className="mt-1.5 rounded-lg bg-surface px-2.5 py-2">
          <Tijdlijn items={data} laden={isLoading} rol="melder" compact />
        </div>
      )}
    </div>
  );
}

/** De laatste kliks van de melder (tech_info.kliks), ingeklapt tot één regel. */
export function KliksCompact({ kliks }: { kliks: KlikContext[] | undefined }) {
  if (!kliks?.length) return null;
  const laatste = kliks[kliks.length - 1];
  const tijd = (iso: string) => {
    const d = new Date(iso);
    return Number.isNaN(d.getTime())
      ? ''
      : d.toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit' });
  };
  return (
    <details className="text-[11px] text-foreground-tertiary mt-0.5">
      <summary className="cursor-pointer inline-flex items-center gap-1 hover:text-foreground">
        <MousePointerClick className="h-3 w-3" />
        Laatste {kliks.length} kliks · laatst: {laatste.scherm ?? '?'} › {laatste.knop}
      </summary>
      <ol className="mt-1 ml-4 space-y-0.5">
        {kliks.map((k, i) => (
          <li key={i}>
            {tijd(k.tijd)} · {k.scherm ?? '?'} › {k.knop}
          </li>
        ))}
      </ol>
    </details>
  );
}
